import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, FileDown, MessageCircle, Sparkles, Eye, Share2, Crown, ArrowRight } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  Bar, BarChart, XAxis, YAxis, Cell, Tooltip,
} from "recharts";
import { api, getUser } from "@/lib/api";
import { downloadPdfWithProgress } from "@/lib/pdfDownload";
import BrandHeader from "@/components/BrandHeader";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import ShareableCertificate from "@/components/ShareableCertificate";
import { TESTIDS } from "@/constants/testIds";
import { formatDate, levelBadgeClass } from "@/lib/format";
import { renderRich } from "@/lib/renderRich";
import { CadenaBlock } from "@/components/cadena/CadenaBlock";

const LEVEL_COLORS = {
  "Crítico": "#EF4444",
  "En desarrollo": "#F59E0B",
  "Funcional": "#3B82F6",
  "Sólido": "#10B981",
};

const AREA_SHORT_NAMES = {
  A: "Marca",
  B: "Selección",
  C: "Onboarding",
  D: "Evaluación",
  E: "Feedback",
  F: "Desarrollo",
};

function RadarAreaTick(props) {
  const { x, y, textAnchor, payload } = props;
  const code = payload?.value;
  const short = AREA_SHORT_NAMES[code] || "";
  return (
    <g>
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill="#0f6e56"
        fontSize={14}
        fontWeight={700}
        dominantBaseline="middle"
      >
        {code}
      </text>
      <text
        x={x}
        y={y + 15}
        textAnchor={textAnchor}
        fill="#6b7280"
        fontSize={10.5}
        fontWeight={500}
        dominantBaseline="middle"
      >
        {short}
      </text>
    </g>
  );
}

const PERSONALIZATION_TIPS = [
  "Un onboarding estructurado de 90 días reduce a la mitad el tiempo hasta la autonomía del nuevo empleado.",
  "Las empresas con feedback sistemático retienen un 34% más de talento sénior que las que no lo tienen.",
  "Documentar el perfil ideal antes de publicar la vacante reduce en un 40% las malas contrataciones.",
  "La marca empleadora empieza dentro: los propios empleados son el mejor canal de atracción.",
  "Un plan de carrera visible por escrito puede aumentar la retención en más del 50%.",
  "Evaluar el desempeño con criterios explícitos elimina casi por completo las percepciones de sesgo.",
  "El 70% de las pymes tecnológicas sin proceso de onboarding pierden al nuevo empleado en los primeros 6 meses.",
  "Definir los primeros objetivos del nuevo empleado por escrito duplica la probabilidad de éxito el primer año.",
];

/**
 * Parte párrafos muy largos en bloques de máx. 3-4 oraciones para mejorar
 * la legibilidad en móvil. Respeta los saltos dobles (`\n\n`) del LLM si ya
 * los emite. Solo aplica la partición automática a párrafos con >4 oraciones.
 */
function splitLongParagraphs(text) {
  if (!text) return [];
  // 1) respetar los \n\n existentes
  const raw = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const out = [];
  for (const para of raw) {
    const sentences = para
      .split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡«"])/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length <= 4) {
      out.push(para);
      continue;
    }
    // >4 oraciones → agrupar de 3 en 3
    const chunk = 3;
    for (let i = 0; i < sentences.length; i += chunk) {
      out.push(sentences.slice(i, i + chunk).join(" "));
    }
  }
  return out;
}


export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState({ free_call_url: "" });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTR, setDownloadingTR] = useState(false);
  // Flag: el usuario pidió descargar el Team Readiness y recibimos 409. En
  // cuanto el polling vea `team_readiness_status === "ready"`, auto-llamamos
  // a `downloadTeamReadinessPdf()` sin requerir otro click del usuario.
  const [pendingTrDownload, setPendingTrDownload] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showShare, setShowShare] = useState(false);
  // Fase educativa del banner mientras la personalización está pending:
  // 0-8s → micro-benchmark, después tips rotando cada 7s.
  const [pendingElapsedS, setPendingElapsedS] = useState(0);
  // Benchmark por país (Fase 2b): se resuelve desde localStorage o desde
  // navigator.language como default. Persiste al cambiarlo.
  const [benchmarks, setBenchmarks] = useState(null);
  const [benchmarkCountry, setBenchmarkCountry] = useState(() => {
    try {
      const stored = localStorage.getItem("thc_benchmark_country");
      if (stored) return stored;
    } catch { /* ignore */ }
    const loc = (typeof navigator !== "undefined" && navigator.language) || "es-ES";
    const region = (loc.split("-")[1] || "").toUpperCase();
    const known = ["ES", "MX", "CO", "AR", "CL", "PE", "UY", "US"];
    return known.includes(region) ? region : "ES";
  });

  useEffect(() => {
    let cancelled = false;
    async function loadBench() {
      try {
        const r = await api.get(`/diagnostic/benchmarks?country=${encodeURIComponent(benchmarkCountry)}`);
        if (!cancelled) setBenchmarks(r.data);
      } catch { /* silencioso: la UI cae al fallback */ }
    }
    loadBench();
    try { localStorage.setItem("thc_benchmark_country", benchmarkCountry); } catch { /* ignore */ }
    return () => { cancelled = true; };
  }, [benchmarkCountry]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    async function load(initial = false) {
      try {
        const [detail, cfg] = await Promise.all([
          api.get(`/diagnostic/${id}`),
          initial ? api.get("/config/public") : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setData(detail.data);
        if (cfg) setConfig(cfg.data);
        // Sigue polling si CUALQUIERA de las dos personalizaciones sigue pendiente.
        const stillPending =
          detail.data.personalization_status === "pending" ||
          detail.data.team_readiness_status === "pending";
        if (stillPending) {
          pollTimer = setTimeout(() => load(false), 3000);
        }
      } catch (_e) {
        if (initial) {
          toast.error("No pudimos cargar el resultado");
          navigate("/dashboard");
        }
      } finally {
        if (initial) setLoading(false);
      }
    }
    load(true);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id, navigate]);

  async function downloadPdf() {
    setDownloading(true);
    try {
      await downloadPdfWithProgress({
        url: `/diagnostic/${id}/pdf`,
        filename: `talent-health-check.pdf`,
        kind: "std",
        label: "Preparando tu informe",
      });
    } catch (err) {
      console.error("PDF download failed", {
        status: err?.response?.status,
        code: err?.code,
        message: err?.message,
      });
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        toast.error("La generación tardó demasiado. Vuelve a intentarlo en unos segundos.");
      } else {
        toast.error("No pudimos generar el PDF");
      }
    } finally {
      setDownloading(false);
    }
  }

  async function downloadTeamReadinessPdf() {
    setDownloadingTR(true);
    try {
      await downloadPdfWithProgress({
        url: `/diagnostic/${id}/team-readiness-pdf`,
        filename: `team-readiness-report.pdf`,
        kind: "tr",
        label: "Preparando Team Readiness Report",
      });
    } catch (e) {
      // 409 → el LLM aún se está generando. Refrescamos el estado para que
      // el polling siga y el botón muestre "Generando…" hasta que esté listo.
      // Además marcamos `pendingTrDownload=true` para que, cuando el polling
      // detecte `ready`, auto-descarguemos sin requerir otro click.
      if (e?.response?.status === 409) {
        toast.message("Tu informe se está generando, te lo descargamos en cuanto esté listo");
        setData((d) => (d ? { ...d, team_readiness_status: "pending" } : d));
        setPendingTrDownload(true);
        try {
          const detail = await api.get(`/diagnostic/${id}`);
          setData(detail.data);
        } catch (_e) {}
      } else if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
        console.error("Team Readiness PDF timeout", { code: e?.code, message: e?.message });
        toast.error("La generación tardó demasiado. Vuelve a intentarlo en unos segundos.");
      } else {
        console.error("Team Readiness PDF failed", {
          status: e?.response?.status,
          code: e?.code,
          message: e?.message,
        });
        toast.error("No pudimos generar el Team Readiness Report");
      }
    } finally {
      setDownloadingTR(false);
    }
  }

  // Timer que va contando segundos mientras la personalización está pendiente.
  // Debe declararse ANTES de cualquier early-return (regla de hooks).
  const isPending = data?.personalization_status === "pending";
  // Team Readiness: sólo bloquear el botón mientras el LLM está en curso.
  // Diagnósticos antiguos (status null) permiten click → dispara generación on-demand.
  const trPending = data?.team_readiness_status === "pending";
  const trReady = data?.team_readiness_status === "ready";

  // Auto-descarga: si el usuario pulsó "Team Readiness Report" mientras el
  // LLM estaba generándose (recibió 409 y `pendingTrDownload=true`), en
  // cuanto el polling detecte `ready` disparamos la descarga sin requerir
  // otro click. Esto arregla el bug en producción donde el informe "nunca
  // se descargaba" porque el usuario cerraba la pestaña o no se daba cuenta
  // de que debía re-pulsar.
  useEffect(() => {
    if (!trReady || !pendingTrDownload || downloadingTR) return;
    setPendingTrDownload(false);
    toast.success("Tu Team Readiness Report ya está listo, descargando…");
    downloadTeamReadinessPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trReady, pendingTrDownload]);
  const trFailed = data?.team_readiness_status === "failed";
  useEffect(() => {
    if (!pendingTrDownload) return undefined;
    const t = setInterval(async () => {
      try {
        const detail = await api.get(`/diagnostic/${id}`);
        setData(detail.data);
      } catch (_e) {}
    }, 3000);
    return () => clearInterval(t);
  }, [pendingTrDownload, id]);
  useEffect(() => {
    if (!trFailed || !pendingTrDownload) return;
    setPendingTrDownload(false);
    toast.error("No pudimos generar tu Team Readiness Report. Pulsa de nuevo el botón para reintentarlo.");
  }, [trFailed, pendingTrDownload]);
  useEffect(() => {
    if (!isPending) return undefined;
    setPendingElapsedS(0);
    const started = Date.now();
    const tick = setInterval(() => {
      setPendingElapsedS(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [isPending]);

  // Counter animado del score: cuenta de 0 hasta overall_score en 1.4s
  // usando ease-out cubic. Se dispara la primera vez que se ve el score.
  const [displayScore, setDisplayScore] = useState(0);
  useEffect(() => {
    if (!data?.overall_score && data?.overall_score !== 0) return undefined;
    if (loading) return undefined;
    const target = data.overall_score;
    const duration = 1400;
    const started = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplayScore(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [data?.overall_score, loading]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <BrandHeader />
        <div className="max-w-3xl mx-auto p-12 text-gray-500">Cargando…</div>
      </div>
    );
  }

  const radarData = data.area_rows.map((r) => ({
    area: `${r.code}`,
    fullName: r.name,
    score: r.score,
    fullMark: 20,
  }));

  const barData = data.area_rows.map((r) => ({
    name: `${r.code} · ${r.name.split(" ")[0]}`,
    score: r.score,
    level: r.level,
  }));

  // Fase 1 (0-8s) = micro-benchmark personalizado al score.
  // Fase 2 (8s+) = tips rotando cada 7s.
  let bannerLine = null;
  if (isPending) {
    if (pendingElapsedS < 8) {
      bannerLine = (
        <>
          Basamos tu análisis en <strong>más de 500 diagnósticos previos</strong> de
          pymes tecnológicas comparables a la tuya.
        </>
      );
    } else {
      const tipIdx = Math.floor((pendingElapsedS - 8) / 7) % PERSONALIZATION_TIPS.length;
      bannerLine = <>💡 {PERSONALIZATION_TIPS[tipIdx]}</>;
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid={TESTIDS.results.root}>
      <BrandHeader
        right={
          <button className="btn-ghost" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4" /> Volver al dashboard
          </button>
        }
      />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <button
          onClick={() => navigate("/dashboard")}
          className="md:hidden inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0F6E56] mb-5 -mt-2 group transition-colors"
          data-testid="results-back-mobile-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          Volver al dashboard
        </button>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 fade-in-up">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
              Resultados · {formatDate(data.created_at)}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900">
              Diagnóstico completo
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className="btn-secondary"
              onClick={() => setShowPreview(true)}
              disabled={isPending}
              data-testid="results-preview-pdf-btn"
              title={isPending ? "Disponible cuando termine la personalización" : "Ver informe sin descargar"}
            >
              <Eye className="w-4 h-4" strokeWidth={1.75} /> Vista previa
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowShare(true)}
              disabled={isPending}
              data-testid="results-share-btn"
              title={isPending ? "Disponible cuando termine la personalización" : "Compartir en redes sociales"}
            >
              <Share2 className="w-4 h-4" strokeWidth={1.75} /> Compartir
            </button>
            <button
              className="btn-primary"
              onClick={downloadPdf}
              disabled={downloading || isPending}
              data-testid={TESTIDS.results.downloadPdfBtn}
              title={isPending ? "Disponible cuando termine la personalización" : ""}
            >
              <FileDown className="w-4 h-4" /> {downloading ? "Generando…" : "Descargar PDF"}
            </button>
            <button
              className="btn-secondary"
              onClick={downloadTeamReadinessPdf}
              disabled={downloadingTR || trPending}
              data-testid="results-download-team-readiness-btn"
              title={
                trPending
                  ? "Tu informe se está generando, esto puede tardar unos segundos"
                  : "Versión ~9 páginas orientada a inversores y comités"
              }
            >
              <FileDown className="w-4 h-4" strokeWidth={1.75} />
              {downloadingTR || trPending ? "Generando…" : "Team Readiness Report"}
            </button>
          </div>
        </div>

        {/* CTA a Consultoría Premium — solo para usuarios que ya tienen acceso
            a ambos productos (THC + Consulting). Se muestra en la cabecera para
            que puedan saltar directo desde el resultado gratuito. */}
        {(() => {
          const user = getUser();
          const hasConsulting = (user?.products || []).includes("consulting") || !!user?.is_admin;
          if (!hasConsulting || isPending) return null;
          return (
            <div
              className="mb-6 rounded-2xl px-5 py-5 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden fade-in-up"
              style={{
                background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
                boxShadow: "0 20px 45px -25px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.25) inset",
              }}
              data-testid="results-open-consulting-cta"
            >
              <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
              <div className="flex items-start gap-3 sm:contents">
                <div
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)" }}
                >
                  <Crown className="w-5 h-5" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-display text-[15px] sm:text-base font-bold leading-tight" style={{ color: "#E5C874" }}>
                      Ya tienes tu Consultoría Premium activada
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#C9A84C", color: "#0b3d2e" }}>
                      Premium
                    </span>
                  </div>
                  <p className="text-[13px] leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Accede al análisis extendido, al plan a 30/90/180 días y al set operativo de 7 piezas que necesitas para desplegar la estrategia.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/consulting")}
                className="group inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap transition-all hover:-translate-y-0.5 flex-shrink-0 w-full sm:w-auto"
                style={{
                  background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                  color: "#0b3d2e",
                  boxShadow: "0 8px 22px -8px rgba(201,168,76,0.55)",
                }}
                data-testid="results-open-consulting-btn"
              >
                Abre tu consultoría
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
              </button>
            </div>
          );
        })()}

        {/* Banner explicativo del Team Readiness Report. Se muestra siempre en
            la página de resultados: el usuario ya ve el botón, aquí entiende
            para qué le sirve antes de descargarlo. */}
        <div
          className="mb-6 rounded-2xl border px-6 py-5 flex items-start gap-4 fade-in-up"
          style={{
            borderColor: "#a7f3d0",
            background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
          }}
          data-testid="team-readiness-info-banner"
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
            style={{ background: "#0f6e56" }}
          >
            <Briefcase className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-display text-base font-bold text-slate-900">
                Team Readiness Report
              </h3>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                Nuevo
              </span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Una versión de tu diagnóstico pensada para{" "}
              <strong>inversores, comités y procesos de due diligence</strong>: traduce cada
              área a lenguaje de riesgo de ejecución y coste de negocio. Descárgalo cuando
              quieras enseñar a un tercero la solidez de tu equipo.
            </p>
          </div>
        </div>

        {isPending && (
          <div
            className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-5 flex items-start gap-4"
            data-testid="personalization-pending-banner"
            role="status"
            aria-live="polite"
          >
            <div className="relative w-8 h-8 flex-shrink-0 mt-0.5">
              <div className="absolute inset-0 rounded-full border-2 border-emerald-200"></div>
              <div className="absolute inset-0 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14.5px] text-emerald-900 leading-relaxed">
                <strong>El sistema OptimizaRRHH está redactando tu informe personalizado.</strong>{" "}
                Ya casi estamos.
              </div>
              {/* Progress checklist honesto: refleja las 4 llamadas LLM paralelas.
                  Timeline aproximado: 0-6s analizando, 6-14s redactando áreas,
                  14-22s plan, 22-28s pulido. */}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="personalization-progress-steps">
                {[
                  { label: "Analizando puntuación global", doneAt: 6 },
                  { label: "Redactando áreas A · B · C", doneAt: 14 },
                  { label: "Redactando áreas D · E · F", doneAt: 20 },
                  { label: "Preparando plan de acción", doneAt: 26 },
                ].map((step) => {
                  const done = pendingElapsedS >= step.doneAt;
                  const active = !done && pendingElapsedS >= Math.max(0, step.doneAt - 6);
                  return (
                    <div key={step.label} className="flex items-center gap-2 text-[12.5px]">
                      {done ? (
                        <span className="w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                      ) : active ? (
                        <span className="w-4 h-4 rounded-full border-2 border-emerald-600 border-t-transparent animate-spin flex-shrink-0"></span>
                      ) : (
                        <span className="w-4 h-4 rounded-full border-2 border-emerald-200 flex-shrink-0"></span>
                      )}
                      <span className={done ? "text-emerald-900 font-medium" : active ? "text-emerald-900" : "text-emerald-800/50"}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div
                key={`tip-${pendingElapsedS < 8 ? 0 : Math.floor((pendingElapsedS - 8) / 7) + 1}`}
                className="text-[13.5px] text-emerald-800/85 leading-relaxed mt-3 pt-3 border-t border-emerald-200/60 fade-in-up"
                data-testid="personalization-pending-tip"
              >
                {bannerLine}
              </div>
            </div>
          </div>
        )}

        {/* Hero score con anillos decorativos y chip glass. */}
        <div
          className="relative rounded-3xl overflow-hidden fade-in-up shadow-[0_20px_60px_-20px_rgba(15,110,86,0.45)]"
          style={{ background: "linear-gradient(135deg, #0f6e56 0%, #178A6C 55%, #34d399 100%)" }}
        >
          <div className="relative p-8 md:p-12 text-white grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.24em] opacity-85 font-medium">
                Puntuación global
              </div>
              <div
                className="font-display text-7xl md:text-[96px] font-bold leading-none mt-3 tracking-tight"
                data-testid={TESTIDS.results.overallScore}
              >
                {displayScore}
                <span className="text-2xl md:text-3xl opacity-80 font-semibold ml-1">/120</span>
              </div>
              <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25">
                <span
                  className={`w-2 h-2 rounded-full ${
                    data.overall_level === "Sólido"
                      ? "bg-emerald-300"
                      : data.overall_level === "Funcional"
                      ? "bg-blue-300"
                      : data.overall_level === "En desarrollo"
                      ? "bg-amber-300"
                      : "bg-red-300"
                  }`}
                ></span>
                <span className="text-[13px] font-medium">Nivel {data.overall_level}</span>
              </div>
            </div>
            <div
              className="text-[15px] leading-relaxed opacity-95 max-w-md"
              style={{ textAlign: "justify", hyphens: "auto" }}
            >
              {isPending ? (
                <div className="space-y-2" data-testid="skeleton-hero">
                  <div className="h-3 rounded bg-white/25 animate-pulse w-full"></div>
                  <div className="h-3 rounded bg-white/25 animate-pulse w-11/12"></div>
                  <div className="h-3 rounded bg-white/25 animate-pulse w-9/12"></div>
                </div>
              ) : (
                <>{data.overall_interpretation.split(". ").slice(0, 2).join(". ")}.</>
              )}
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
          {data.cadena ? (
          <div className="card lg:col-span-12" data-testid="results-cadena">
            <CadenaBlock cadena={data.cadena} />
          </div>
          ) : (
          <div className="card lg:col-span-6 fade-in-up" data-testid={TESTIDS.results.radar}>
            <h3 className="font-display text-lg font-bold text-gray-900 mb-1">
              Vista radar por área
            </h3>
            <p className="text-xs text-gray-500 mb-4">Puntuación de cada dimensión sobre 20 puntos.</p>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} outerRadius="68%" margin={{ top: 12, right: 40, bottom: 12, left: 40 }}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="area" tick={<RadarAreaTick />} tickLine={false} />
                  <PolarRadiusAxis angle={90} domain={[0, 20]} tick={{ fill: "#9ca3af", fontSize: 10 }} />
                  <Radar name="Score" dataKey="score" stroke="#0f6e56" strokeWidth={2} fill="#0f6e56" fillOpacity={0.22} />
                  <Tooltip
                    formatter={(v) => `${v}/20`}
                    labelFormatter={(l, p) => (p?.[0]?.payload?.fullName ? p[0].payload.fullName : l)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}

          <div className={`card ${data.cadena ? "lg:col-span-12" : "lg:col-span-6"} fade-in-up`}>
            <h3 className="font-display text-lg font-bold text-gray-900 mb-1">Barras por área</h3>
            <p className="text-xs text-gray-500 mb-4">Color según nivel.</p>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={barData} layout="vertical" margin={{ left: 12, right: 20 }}>
                  <XAxis type="number" domain={[0, 20]} tick={{ fill: "#9ca3af", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fill: "#4b5563", fontSize: 12 }} />
                  <Tooltip
                    formatter={(v) => `${v}/20`}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb", fontSize: 12 }}
                  />
                  <Bar dataKey="score" radius={[0, 8, 8, 0]}>
                    {barData.map((entry, index) => (
                      <Cell key={`c-${index}`} fill={LEVEL_COLORS[entry.level] || "#0f6e56"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Areas detail */}
        <div className="mt-14 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <h2 className="font-display text-2xl font-bold text-gray-900">
            Interpretación por área
          </h2>
          {benchmarks?.available_countries?.length > 0 && (
            <div className="flex items-center gap-2 text-sm" data-testid="results-benchmark-selector">
              <span className="text-gray-500">Benchmark:</span>
              <select
                value={benchmarkCountry}
                onChange={(e) => setBenchmarkCountry(e.target.value)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-semibold text-[#0f6e56] focus:outline-none focus:ring-2 focus:ring-[#0f6e56]/25"
                data-testid="results-benchmark-country-select"
              >
                {benchmarks.available_countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="space-y-4">
          {data.area_rows.map((r, i) => (
            <div
              key={r.code}
              className="card fade-in-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {/* Header de área: en móvil apila badge/score arriba y título abajo full-width */}
              <div className="flex items-start justify-between gap-3 mb-3 sm:mb-0 sm:flex-wrap sm:gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#0f6e56]/8 text-[#0f6e56] font-display font-bold flex items-center justify-center flex-shrink-0">
                  {r.code}
                </div>
                <div className="hidden sm:block flex-1 min-w-0">
                  <h3 className="font-display text-lg font-bold text-gray-900">{r.name}</h3>
                  <p className="text-xs text-gray-500 italic mt-0.5">{r.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-2xl sm:text-3xl font-display font-bold text-[#0f6e56] leading-none">
                    {r.score}
                    <span className="text-base text-gray-400 font-semibold">/20</span>
                  </div>
                  <span className={`${levelBadgeClass(r.level)} mt-2`}>{r.level}</span>
                </div>
              </div>
              {/* Título full-width solo en móvil (en desktop ya está renderizado arriba) */}
              <div className="sm:hidden mb-1">
                <h3 className="font-display text-lg font-bold text-gray-900 leading-tight">{r.name}</h3>
                <p className="text-xs text-gray-500 italic mt-1">{r.description}</p>
              </div>
              <div className="mt-4 space-y-3">
                {isPending ? (
                  <div className="space-y-2" data-testid={`skeleton-area-${r.code}`}>
                    <div className="h-3 rounded bg-gray-200 animate-pulse w-full"></div>
                    <div className="h-3 rounded bg-gray-200 animate-pulse w-11/12"></div>
                    <div className="h-3 rounded bg-gray-200 animate-pulse w-10/12"></div>
                    <div className="h-3 rounded bg-gray-200 animate-pulse w-8/12"></div>
                  </div>
                ) : (
                  splitLongParagraphs(r.interpretation).map((para, pi) => (
                    <p
                      key={pi}
                      className="text-[15px] text-gray-700 leading-relaxed text-left sm:text-justify"
                      style={{ hyphens: "auto" }}
                    >
                      {renderRich(para.trim())}
                    </p>
                  ))
                )}
              </div>
              {benchmarks?.areas?.[r.code] && (
                <div
                  className="mt-4 flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 rounded-xl px-3.5 py-2.5 border border-[#0f6e56]/12 bg-gradient-to-r from-[#0f6e56]/[0.04] via-[#c9a84c]/[0.04] to-transparent"
                  data-testid={`results-benchmark-area-${r.code}`}
                >
                  <span className="inline-flex self-start items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#0f6e56] font-bold whitespace-nowrap sm:pt-0.5">
                    <span aria-hidden className="inline-block w-1 h-1 rounded-full bg-[#c9a84c] sm:hidden" />
                    Benchmark {benchmarks.name}
                  </span>
                  <span className="text-[13px] leading-relaxed text-gray-600 italic sm:flex-1">
                    {benchmarks.areas[r.code]}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Overall */}
        <div className="mt-14 rounded-3xl bg-white border border-gray-100 p-8 md:p-10 fade-in-up">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
            Interpretación general
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Nivel {data.overall_level} · {data.overall_score}/120
          </h2>
          <div className="space-y-5">
            {isPending ? (
              <div className="space-y-2" data-testid="skeleton-overall">
                <div className="h-3 rounded bg-gray-200 animate-pulse w-full"></div>
                <div className="h-3 rounded bg-gray-200 animate-pulse w-11/12"></div>
                <div className="h-3 rounded bg-gray-200 animate-pulse w-full"></div>
                <div className="h-3 rounded bg-gray-200 animate-pulse w-10/12"></div>
                <div className="h-3 rounded bg-gray-200 animate-pulse w-9/12"></div>
                <div className="h-3 rounded bg-gray-200 animate-pulse w-11/12"></div>
              </div>
            ) : (
              /*
               * Estrategia equilibrada: respetamos primero los párrafos que
               * marque el LLM (\n\n). Dentro de cada uno, agrupamos las
               * frases de 2 en 2 para dar aire sin trocear en exceso.
               */
              (() => {
                const raw = data.overall_interpretation || "";
                const originalParas = raw.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
                const paragraphs = [];
                for (const block of originalParas) {
                  const sentences = block.match(/[^.!?]+[.!?]+["»)\]]*\s*/g);
                  if (sentences && sentences.length > 2) {
                    for (let i = 0; i < sentences.length; i += 2) {
                      paragraphs.push(sentences.slice(i, i + 2).join(" ").trim());
                    }
                  } else {
                    paragraphs.push(block);
                  }
                }
                return paragraphs.map((para, i) => (
                  <p
                    key={i}
                    className="text-[15px] leading-[1.75] text-gray-700"
                    style={{ textAlign: "justify", hyphens: "auto" }}
                  >
                    {renderRich(para)}
                  </p>
                ));
              })()
            )}
          </div>
        </div>

        {/* Recomendaciones prácticas */}
        {isPending ? (
          <div className="mt-8 fade-in-up" data-testid="skeleton-recommendations">
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
              Recomendaciones prácticas
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Por dónde empezar esta semana
            </h2>
            <div className="space-y-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-3xl bg-white border border-gray-100 p-7 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gray-100 animate-pulse flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 rounded bg-gray-200 animate-pulse w-1/3"></div>
                      <div className="h-3 rounded bg-gray-200 animate-pulse w-full"></div>
                      <div className="h-3 rounded bg-gray-200 animate-pulse w-11/12"></div>
                    </div>
                  </div>
                  <div className="mt-5 space-y-3">
                    {[0, 1, 2].map((si) => (
                      <div key={si} className="flex gap-3 items-start">
                        <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse flex-shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-3 rounded bg-gray-200 animate-pulse w-full"></div>
                          <div className="h-3 rounded bg-gray-200 animate-pulse w-10/12"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : data.recommendations && data.recommendations.cards && data.recommendations.cards.length > 0 && (
          <div className="mt-8 fade-in-up" data-testid="results-recommendations">
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0f6e56] mb-3">
              {data.recommendations.mode === "maintenance"
                ? "Modo mantenimiento"
                : "Recomendaciones prácticas"}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {data.recommendations.mode === "maintenance"
                ? "Cómo proteger lo que ya funciona"
                : "Por dónde empezar esta semana"}
            </h2>
            {data.recommendations.intro && (
              <p
                className="text-[15px] leading-relaxed text-gray-700 mb-6 pr-8 md:pr-10"
                style={{ textAlign: "justify", hyphens: "auto" }}
              >
                {renderRich(data.recommendations.intro)}
              </p>
            )}
            <div className="space-y-5">
              {data.recommendations.cards.map((card, ci) => (
                <div
                  key={ci}
                  className="rounded-3xl bg-white border border-gray-100 p-7 md:p-8"
                  data-testid={`recommendation-card-${card.code || ci}`}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {card.code && (
                      <div className="w-11 h-11 rounded-xl bg-[#0f6e56]/8 text-[#0f6e56] font-display font-bold flex items-center justify-center flex-shrink-0">
                        {card.code}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display text-lg font-bold text-gray-900 flex-1 min-w-0 leading-tight">
                          {card.name || card.title}
                        </h3>
                        {card.score !== undefined && (
                          <div className="text-right flex-shrink-0 pt-0.5">
                            <div className="text-2xl font-display font-bold text-[#0f6e56] leading-none">
                              {card.score}
                              <span className="text-sm text-gray-400 font-semibold">/20</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {card.reason && (
                        <p
                          className="text-[14px] text-gray-600 mt-2 leading-relaxed"
                          style={{ textAlign: "justify", hyphens: "auto" }}
                        >
                          {renderRich(card.reason)}
                        </p>
                      )}
                    </div>
                  </div>
                  <ol className="mt-5 space-y-3 list-none pl-0">
                    {(card.steps || []).map((step, si) => (
                      <li key={si} className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#0f6e56] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                          {si + 1}
                        </span>
                        <span
                          className="text-[14.5px] text-gray-700 leading-relaxed"
                          style={{ textAlign: "justify", hyphens: "auto" }}
                        >
                          {renderRich(step)}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Free WhatsApp call - only for scores below 85 (Crítico / En desarrollo) */}
        {data.overall_score < 85 && config.free_call_url && (
          <div
            className="mt-8 rounded-3xl overflow-hidden border border-emerald-100 fade-in-up"
            style={{
              background:
                "linear-gradient(135deg, #ecfdf5 0%, #ffffff 55%, #f0fdf4 100%)",
            }}
            data-testid="results-free-call-block"
          >
            <div className="p-8 md:p-10 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-3">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-emerald-700 bg-emerald-100/70 px-3 py-1 rounded-full">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Recomendado para ti
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mt-4 leading-tight">
                  Reserva una llamada gratis de 15 minutos
                </h3>
                <p
                  className="text-[15px] text-gray-700 mt-3 leading-relaxed max-w-lg"
                  style={{ textAlign: "justify", hyphens: "auto" }}
                >
                  Tu puntuación indica que hay áreas con margen de mejora significativo.
                  Cuéntanos tu caso por WhatsApp y te orientamos sin compromiso sobre por
                  dónde empezar a construir un sistema sólido de gestión del talento.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <a
                  href={config.free_call_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-white font-semibold text-[15px] shadow-lg shadow-emerald-500/25 hover:-translate-y-0.5 hover:shadow-emerald-500/40 transition-transform"
                  style={{ background: "#25D366" }}
                  data-testid="results-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" strokeWidth={2} />
                  Escríbenos por WhatsApp
                </a>
                <div className="text-xs text-emerald-800/70 mt-3">
                  Respuesta habitual en menos de 24h laborables.
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {showPreview && (
        <PdfPreviewModal
          diagnosticId={id}
          filename={`talent-health-check-${data.created_at?.slice(0, 10)}.pdf`}
          onClose={() => setShowPreview(false)}
        />
      )}
      {showShare && (
        <ShareableCertificate
          data={data}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
}
